import type { AutoMessageType } from "@/types/messages.ts";

/**
 * The user-facing line for each auto message — "You approved the invitation",
 * "You cancelled an email draft". This copy is authored, not derived from the
 * type name, and it is what the user reads to understand what they just did;
 * shared so web's chip and native's bubble always spell an action the same way.
 *
 * Icons stay per-platform (web pairs these with `lucide-react` plus its own
 * custom Reply/Forward, native with `lucide-react-native`), so only the strings
 * live here — see each project's `autoActionIcon`.
 */
export const autoActionText: Record<AutoMessageType, string> = {
  autoLearnMore: "More details about this topic",
  autoReply: "You initiated a reply",
  autoReplyAll: "You initiated a reply",
  autoForward: "You requested to forward this message",
  autoMemoryConfirmed: "Memory confirmed",
  autoSendEmailDraft: "You approved and sent an email",
  autoCancelEmailDraft: "You cancelled an email draft",
  autoSendInvitation: "You approved the invitation",
  autoCancelInvitation: "You cancelled an invitation",
  autoAcceptRsvp: "You accepted an invitation",
  autoDeclineRsvp: "You declined an invitation",
  autoTentativeRsvp: "You tentatively accepted an invitation",
  autoApplyEventChange: "You approved an event change",
  autoCancelEventChange: "You cancelled an event change",
  autoApplyContactChange: "You approved a contact change",
  autoCancelContactChange: "You discarded a contact change",
  autoMakePhoneCall: "You approved making a phone call",
  autoCancelPhoneCall: "You cancelled making a phone call",
  autoAskUserFormSubmit: "You clarified some information",
  autoExecuteBrief: "You executed a brief",
  autoCancelBrief: "You cancelled a brief",
  autoApproveScheduling: "You approved a scheduling suggestion",
  autoCounterProposeScheduling: "You declined the meeting and asked to reschedule the meeting",
  autoCancelScheduling: "You cancelled a scheduling suggestion",
  autoApproveSchedulingKickoff: "You approved scheduling a meeting",
  autoCancelSchedulingKickoff: "You cancelled scheduling a meeting",
  autoApproveRoutine: "You approved a routine",
  autoCancelRoutine: "You cancelled a routine",
  autoApproveDocument: "You approved a document",
  autoApproveSkill: "You approved a skill",
  autoCancelSkill: "You discarded a skill",
  autoApproveApplet: "You approved an applet",
  autoTaskCompleted: "You marked a task done",
  autoTaskCancelled: "You cancelled a task",
  autoTaskReopened: "You reopened a task",
  autoSendExternalMessage: "You sent the message",
  autoCancelExternalMessage: "You cancelled the message",
  autoOAuthConnected: "Account connected successfully",
  autoConnectorPickerDone: "Finished connecting services",
  autoPickerDone: "Picking done",
  // Covers an expiry as well as a cancellation, and the server distinguishes
  // them in the message content — which this label is rendered instead of. Until
  // that changes it must not claim the user did something they did not.
  autoOAuthCancelled: "Account sign-in didn't finish",
  // "Shared" covers both CC and forward — handleAgentCC engages on both
  // (forwards always create a new thread → engagement path).
  autoCommunicationReceived: "You shared an email with Okthx",
  // Normally drawn as a badge on the message it was placed on, not as a row;
  // this line shows only where that message isn't in the feed.
  autoReaction: "You reacted to a message",
};
